import { normalizeCategoryKey, translateError } from './auditDictionary';

/** Priorité des erreurs techniques (tri). */
export const SEV_ORDER = { high: 0, medium: 1, low: 2 };

export const SEV_CONFIG = {
  high: {
    label: 'Critique',
    badge: 'bg-red-100 text-red-700 border-red-200',
    dot: 'bg-red-500',
    counter: 'bg-red-50 border-red-200 text-red-700',
  },
  medium: {
    label: 'Moyenne',
    badge: 'bg-orange-100 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
    counter: 'bg-orange-50 border-orange-200 text-orange-700',
  },
  low: {
    label: 'Mineure',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    dot: 'bg-amber-400',
    counter: 'bg-amber-50 border-amber-200 text-amber-700',
  },
};

export const TYPE_LABELS = {
  seo: 'SEO',
  performance: 'Performance',
  accessibility: 'Accessibilité',
  social: 'Réseaux sociaux',
  security: 'Sécurité',
  mobile: 'Mobile',
};

export function typeLabel(t) {
  return TYPE_LABELS[t] || t || '—';
}

/** Erreurs liées à l'outil (API PageSpeed, mesure impossible) — pas des actions client. */
export function isOperationalTechnicalError(err) {
  if (!err) return false;
  const key = normalizeCategoryKey(err.category);
  if (key === 'erreur pagespeed') return true;
  const rec = (err.recommendation || '').toLowerCase();
  if (rec.includes('clé api pagespeed') || rec.includes('cle api pagespeed')) return true;
  const msg = (err.message || '').toLowerCase();
  if (msg.includes("impossible d'analyser la performance")) return true;
  return false;
}

/**
 * Aplatit la réponse API en lignes de tableau (une ligne = un problème sur une page).
 */
export function flattenTechnicalErrors(errorsData, project) {
  if (!errorsData?.errors?.length) return [];

  const rows = [];
  for (const page of errorsData.errors) {
    for (let i = 0; i < (page.errors || []).length; i++) {
      const err = page.errors[i];
      if (isOperationalTechnicalError(err)) continue;
      rows.push({
        id: `${page.url}::${err.category || err.message || i}`,
        url: page.url,
        severity: err.severity || 'low',
        category: err.category || 'Problème détecté',
        message: err.message || '',
        recommendation: err.recommendation || '',
        type: err.type || 'seo',
        isReference: project?.reference_pages?.includes(page.url),
      });
    }
  }

  return rows.sort((a, b) => {
    const sev = (SEV_ORDER[a.severity] ?? 3) - (SEV_ORDER[b.severity] ?? 3);
    if (sev !== 0) return sev;
    if (a.isReference !== b.isReference) return a.isReference ? -1 : 1;
    return a.category.localeCompare(b.category, 'fr');
  });
}

export function normStr(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function matchesHighlight(text, highlight) {
  if (!highlight) return false;
  return normStr(text).includes(normStr(highlight));
}

/** Clé de regroupement : même type + même catégorie normalisée. */
export function errorGroupKey(row) {
  return `${row.type || 'seo'}::${normalizeCategoryKey(row.category)}`;
}

/**
 * Regroupe les lignes d'erreurs : communes (≥2 pages distinctes) vs spécifiques.
 */
export function partitionTechnicalErrors(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = errorGroupKey(row);
    if (!map.has(key)) {
      map.set(key, {
        key,
        category: row.category,
        type: row.type,
        severity: row.severity,
        recommendation: row.recommendation,
        rows: [],
        urls: new Set(),
      });
    }
    const g = map.get(key);
    g.rows.push(row);
    g.urls.add(row.url);
    if ((SEV_ORDER[row.severity] ?? 3) < (SEV_ORDER[g.severity] ?? 3)) {
      g.severity = row.severity;
    }
    if (!g.recommendation && row.recommendation) g.recommendation = row.recommendation;
  }

  const groups = [...map.values()].map((g) => {
    const translation = translateError(g.rows[0]);
    return {
      ...g,
      pageCount: g.urls.size,
      urls: [...g.urls].sort((a, b) => a.localeCompare(b, 'fr')),
      translation,
      isCommon: g.urls.size >= 2,
    };
  });

  groups.sort((a, b) => {
    const sev = (SEV_ORDER[a.severity] ?? 3) - (SEV_ORDER[b.severity] ?? 3);
    if (sev !== 0) return sev;
    return b.pageCount - a.pageCount;
  });

  return {
    common: groups.filter((g) => g.isCommon),
    specific: groups.filter((g) => !g.isCommon).flatMap((g) => g.rows),
  };
}
